<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue';

const steps = [
  {
    number: '01',
    title: 'Deploy on Clovyr',
    description:
      'Launch Clovyr AI Cloud in one click. Clovyr provisions the compute, storage, and networking on your behalf. You own the infrastructure.',
    tag: '~5 minutes',
  },
  {
    number: '02',
    title: 'Connect Your Models & Docs',
    description:
      'Pick your LLM: local Llama, OpenAI, Claude, Mistral, or any compatible provider. Upload your documents, codebases, or link a data source.',
    tag: 'Any model',
  },
  {
    number: '03',
    title: 'Chat. Build. Stay Private.',
    description:
      'Your whole team gets a private AI workspace. Chat with docs, run agents, and use slash commands, all within infrastructure you control.',
    tag: 'Full control',
  },
];

const sectionVisible = ref(false);
const visibleSteps = ref<boolean[]>(steps.map(() => false));
const sectionRef = ref<HTMLElement | null>(null);
const stepRefs = ref<(HTMLElement | null)[]>([]);

let sectionObserver: IntersectionObserver | undefined;
let stepObserver: IntersectionObserver | undefined;

const setStepRef = (index: number, element: Element | null) => {
  stepRefs.value[index] = element as HTMLElement | null;
};

onMounted(() => {
  sectionObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }

        sectionVisible.value = true;
        sectionObserver?.disconnect();
      });
    },
    {
      threshold: 0.15,
    },
  );

  if (sectionRef.value) {
    sectionObserver.observe(sectionRef.value);
  }

  stepObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }

        const index = Number((entry.target as HTMLElement).dataset.index);
        visibleSteps.value[index] = true;
        stepObserver?.unobserve(entry.target);
      });
    },
    {
      threshold: 0.1,
    },
  );

  stepRefs.value.forEach((step) => {
    if (step) {
      stepObserver?.observe(step);
    }
  });
});

onBeforeUnmount(() => {
  sectionObserver?.disconnect();
  stepObserver?.disconnect();
});
</script>

<template>
  <section ref="sectionRef" class="ai-how">
    <div class="ai-how__inner">
      <header class="ai-how__head" :class="{ 'is-visible': sectionVisible }">
        <p>How it works</p>
        <h2>Up and running in <span>minutes.</span></h2>
      </header>

      <div class="ai-how__steps-wrap">
        <div class="ai-how__line"></div>
        <div class="ai-how__grid">
          <article
            v-for="(step, idx) in steps"
            :key="step.number"
            :ref="(element) => setStepRef(idx, element as Element)"
            class="ai-how__step"
            :class="{ 'is-visible': visibleSteps[idx] }"
            :style="{ '--delay': `${idx * 150}ms` }"
            :data-index="idx"
          >
            <div class="ai-how__num-wrap">
              <div class="ai-how__num">{{ step.number }}</div>
              <div class="ai-how__num-pulse"></div>
            </div>

            <div class="ai-how__tag">
              <span>{{ step.tag }}</span>
            </div>

            <h3>{{ step.title }}</h3>
            <p>{{ step.description }}</p>
          </article>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
@use '../../styles/components/ai-landing-shared' as shared;

.ai-how {
  background: #1e1530;
}

.ai-how__inner {
  @include shared.section-inner(1280px, 7rem 2rem, 5.5rem 1.5rem);
}

.ai-how__head p {
  @include shared.eyebrow;
  text-align: center;
}

.ai-how__head h2 {
  @include shared.section-title;
  margin-bottom: 0;
  text-align: center;
}

.ai-how__head h2 span {
  @include shared.accent-span;
}

.ai-how__head {
  @include shared.section-head(5rem, 20px);
  margin-bottom: 5rem;
}

.ai-how__head.is-visible {
  @include shared.section-head-visible(fadeLift, 0.55s);
}

.ai-how__steps-wrap {
  position: relative;
}

.ai-how__line {
  display: none;
}

.ai-how__grid {
  display: flex;
  flex-direction: column;
  gap: 6rem;
}

.ai-how__step {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  text-align: center;
  opacity: 0;
  transform: translateY(30px);
}

.ai-how__step.is-visible {
  animation: fadeLiftStep 0.6s ease var(--delay, 0ms) forwards;
}

.ai-how__num-wrap {
  position: relative;
  margin-bottom: 2rem;
}

.ai-how__num {
  position: relative;
  z-index: 1;
  display: flex;
  width: 6rem;
  height: 6rem;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  border: 1px solid rgba(0, 191, 116, 0.28);
  background: #2a1f40;
  color: #00bf74;
  font-size: 1.875rem;
  font-weight: 900;
}

.ai-how__num-pulse {
  position: absolute;
  inset: 0;
  border: 1px solid rgba(0, 191, 116, 0.2);
  border-radius: 999px;
  transform: scale(1.25);
  animation: pulseRing 2.2s ease-in-out infinite;
}

.ai-how__tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.25rem;
  padding: 0.25rem 0.75rem;
  border: 1px solid rgba(0, 191, 116, 0.2);
  border-radius: 999px;
  background: rgba(0, 191, 116, 0.1);
}

.ai-how__tag span {
  color: #00bf74;
  font-size: 0.75rem;
  font-weight: 500;
}

.ai-how__step h3 {
  margin: 0 0 0.6rem;
  color: #fff;
  font-size: 1.3rem;
  font-weight: 800;
}

.ai-how__step p {
  margin: 0 auto;
  max-width: 280px;
  color: #6b7280;
  font-size: 0.85rem;
  line-height: 1.65;
  font-weight: 500;
}

@media (min-width: 1024px) {
  .ai-how__line {
    display: block;
    position: absolute;
    left: 14%;
    right: 14%;
    top: 3.25rem;
    height: 1px;
    background: linear-gradient(to right, transparent, rgba(0, 191, 116, 0.28), transparent);
  }

  .ai-how__grid {
    flex-direction: row;
  }
}

@include shared.keyframes-fade-lift(fadeLift, 20px);

@keyframes fadeLiftStep {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulseRing {
  0%,
  100% {
    opacity: 0.6;
    transform: scale(1.25);
  }
  50% {
    opacity: 0.3;
    transform: scale(1.33);
  }
}
</style>
